import React, { useState, useMemo } from 'react';
import { Accordion, Dropdown } from 'react-bootstrap';
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import { MenuSchedule, ScheduleException, WeeklySchedule } from '../../../../../../types/menuSchedule.types';
import { calculateMenuStatus, formatRelativeDateTime } from '../../../../../../utils/menu';
import './ScheduleEditor.scss';

interface ScheduleEditorProps {
  schedule: MenuSchedule | null;
  onChange: (schedule: MenuSchedule | null) => void;
}

// Generate time slots in HH:MM format (00:00 to 23:45 in 15-minute increments)
const generateTimeSlots = () => {
  const slots: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      slots.push(timeStr);
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

const ScheduleEditor: React.FC<ScheduleEditorProps> = ({ schedule, onChange }) => {
  const [activeKey, setActiveKey] = useState<string>('0');

  // Calculate current menu status using local time
  const menuStatus = useMemo(() => calculateMenuStatus(schedule), [schedule]);

  const dayNames: { key: keyof WeeklySchedule; label: string }[] = [
    { key: 'monday', label: 'Lunes' },
    { key: 'tuesday', label: 'Martes' },
    { key: 'wednesday', label: 'Miércoles' },
    { key: 'thursday', label: 'Jueves' },
    { key: 'friday', label: 'Viernes' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' },
  ];

  // Initialize empty schedule
  const getDefaultSchedule = (): MenuSchedule => ({
    weekly_schedule: {
      monday: [{ open: '09:00', close: '22:00' }],
      tuesday: [{ open: '09:00', close: '22:00' }],
      wednesday: [{ open: '09:00', close: '22:00' }],
      thursday: [{ open: '09:00', close: '22:00' }],
      friday: [{ open: '09:00', close: '22:00' }],
      saturday: [{ open: '09:00', close: '22:00' }],
      sunday: [],
    },
    exceptions: [],
  });

  const currentSchedule = schedule || getDefaultSchedule();

  const updateTimeSlot = (
    day: keyof WeeklySchedule,
    index: number,
    field: 'open' | 'close',
    value: string
  ) => {
    const newSchedule = { ...currentSchedule };
    const newSlots = [...newSchedule.weekly_schedule[day]];
    newSlots[index] = { ...newSlots[index], [field]: value };
    newSchedule.weekly_schedule = {
      ...newSchedule.weekly_schedule,
      [day]: newSlots,
    };
    onChange(newSchedule);
  };

  const addTimeSlot = (day: keyof WeeklySchedule) => {
    const newSchedule = { ...currentSchedule };
    const newSlots = [...newSchedule.weekly_schedule[day], { open: '09:00', close: '22:00' }];
    newSchedule.weekly_schedule = {
      ...newSchedule.weekly_schedule,
      [day]: newSlots,
    };
    onChange(newSchedule);
  };

  const removeTimeSlot = (day: keyof WeeklySchedule, index: number) => {
    const newSchedule = { ...currentSchedule };
    const newSlots = newSchedule.weekly_schedule[day].filter((_, i) => i !== index);
    newSchedule.weekly_schedule = {
      ...newSchedule.weekly_schedule,
      [day]: newSlots,
    };
    onChange(newSchedule);
  };

  const addException = () => {
    const newSchedule = { ...currentSchedule };
    const newExceptions = [
      ...(newSchedule.exceptions || []),
      { date: new Date().toISOString().split('T')[0], closed: false, open: '09:00', close: '22:00' },
    ];
    newSchedule.exceptions = newExceptions;
    onChange(newSchedule);
  };

  const updateException = (
    index: number,
    field: keyof ScheduleException,
    value: string | boolean
  ) => {
    const newSchedule = { ...currentSchedule };
    const newExceptions = [...(newSchedule.exceptions || [])];
    newExceptions[index] = { ...newExceptions[index], [field]: value };
    newSchedule.exceptions = newExceptions;
    onChange(newSchedule);
  };

  const removeException = (index: number) => {
    const newSchedule = { ...currentSchedule };
    const newExceptions = (newSchedule.exceptions || []).filter((_, i) => i !== index);
    newSchedule.exceptions = newExceptions;
    onChange(newSchedule);
  };

  const copyScheduleToAll = (sourceDay: keyof WeeklySchedule) => {
    const newSchedule = { ...currentSchedule };
    const sourceSlots = newSchedule.weekly_schedule[sourceDay];
    
    dayNames.forEach(({ key }) => {
      if (key !== sourceDay) {
        newSchedule.weekly_schedule[key] = sourceSlots.map(slot => ({ ...slot }));
      }
    });
    
    onChange(newSchedule);
  };

  return (
    <div className="schedule-editor">
      <p className="schedule-description">
        Configure los horarios semanales y excepciones especiales para este menú.
      </p>

      {/* Schedule Status - Always visible, compact design */}
      {schedule && (
        <div className="schedule-status-compact">
          <div className={`status-badge ${menuStatus.isOpen ? 'open' : 'closed'}`}>
            {menuStatus.isOpen ? '🟢 Abierto' : '🔴 Cerrado'}
          </div>
          <div className="status-details">
            {!menuStatus.isOpen && menuStatus.nextOpening && (
              <span className="next-event">
                Próxima apertura: {formatRelativeDateTime(menuStatus.nextOpening)}
              </span>
            )}
            {menuStatus.isOpen && menuStatus.nextClosing && (
              <span className="next-event">
                Próximo cierre: {formatRelativeDateTime(menuStatus.nextClosing)}
              </span>
            )}
          </div>
        </div>
      )}

      <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k as string)}>
        {/* Weekly Schedule */}
        <Accordion.Item eventKey="1">
          <Accordion.Header>Horario Semanal</Accordion.Header>
          <Accordion.Body>
            <div className="weekly-schedule">
              {dayNames.map(({ key, label }) => (
                <div key={key} className="day-schedule">
                  <div className="day-header">
                    <label className="day-label">{label}</label>
                    <div className="day-actions">
                      {currentSchedule.weekly_schedule[key].length > 0 && (
                        <button
                          type="button"
                          className="copy-schedule-btn"
                          onClick={() => copyScheduleToAll(key)}
                          title="Copiar este horario a todos los días"
                        >
                          Copiar a todos
                        </button>
                      )}
                      <button
                        type="button"
                        className="add-slot-btn"
                        onClick={() => addTimeSlot(key)}
                      >
                        <HiOutlinePlus /> Agregar horario
                      </button>
                    </div>
                  </div>

                  {currentSchedule.weekly_schedule[key].length === 0 ? (
                    <div className="closed-day">Cerrado</div>
                  ) : (
                    <div className="time-slots">
                      {currentSchedule.weekly_schedule[key].map((slot, index) => (
                        <div key={index} className="time-slot">
                          <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" className="time-dropdown-toggle">
                              {slot.open}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="time-dropdown-menu">
                              {timeSlots.map((time) => (
                                <Dropdown.Item
                                  key={time}
                                  onClick={() => updateTimeSlot(key, index, 'open', time)}
                                >
                                  {time}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                          <span className="to-label">—</span>
                          <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" className="time-dropdown-toggle">
                              {slot.close}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="time-dropdown-menu">
                              {timeSlots.map((time) => (
                                <Dropdown.Item
                                  key={time}
                                  onClick={() => updateTimeSlot(key, index, 'close', time)}
                                >
                                  {time}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                          <button
                            type="button"
                            className="remove-slot-btn"
                            onClick={() => removeTimeSlot(key, index)}
                          >
                            <HiOutlineTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {/* Exceptions */}
        {/* 
        <Accordion.Item eventKey="2">
          <Accordion.Header>
            Excepciones ({currentSchedule.exceptions?.length || 0})
          </Accordion.Header>
          <Accordion.Body>
            <div className="exceptions-section">
              <p className="exceptions-description">
                Configure horarios especiales para fechas específicas (feriados, eventos especiales, etc.)
              </p>
              
              <Button
                variant="none"
                className="mp-button light"
                onClick={addException}
              >
                <HiOutlinePlus /> Agregar Excepción
              </Button>

              {currentSchedule.exceptions && currentSchedule.exceptions.length > 0 && (
                <div className="exceptions-list">
                  {currentSchedule.exceptions.map((exception, index) => (
                    <div key={index} className="exception-item">
                      <div className="exception-header">
                        <Form.Control
                          type="date"
                          value={exception.date}
                          onChange={(e) => updateException(index, 'date', e.target.value)}
                        />
                        <Form.Check
                          type="checkbox"
                          label="Cerrado todo el día"
                          checked={exception.closed || false}
                          onChange={(e) => updateException(index, 'closed', e.target.checked)}
                        />
                        <Button
                          variant="none"
                          size="sm"
                          className="remove-exception-btn"
                          onClick={() => removeException(index)}
                        >
                          <HiOutlineTrash />
                        </Button>
                      </div>

                      {!exception.closed && (
                        <div className="exception-times">
                          <Form.Control
                            type="time"
                            value={exception.open || '09:00'}
                            onChange={(e) => updateException(index, 'open', e.target.value)}
                          />
                          <span className="to-label">a</span>
                          <Form.Control
                            type="time"
                            value={exception.close || '22:00'}
                            onChange={(e) => updateException(index, 'close', e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Accordion.Body>
        </Accordion.Item>
        */}
      </Accordion>
    </div>
  );
};

export default ScheduleEditor;
